import React, { useState, useEffect, useRef, useCallback } from "react";
import "../AdminPanel/AdminPanel.css";
import "../AdminPanel/tabs/CRM/CRMModule.css";
import apiClient from "../../api/apiClient";
import Loading from "../../components/Loading/Loading";
import InboundDashboard from "./tabs/InboundDashboard";
import InboundLeadList from "./tabs/InboundLeadList";
import InboundSegments from "./tabs/InboundSegments";
import InboundCampaignTab from "./tabs/InboundCampaignTab";
import AutomationsCanvas from "./tabs/AutomationsCanvas";
import LeadDetail from "../AdminPanel/tabs/CRM/LeadDetail";

const INBOUND_BASE = "/admin/crm/inbound/leads";
const LAST_FLOW_KEY = "crm_automations_last_flow_id";
const SAVE_DEBOUNCE_MS = 600;

const TABS = [
    { key: "dashboard",   label: "Dashboard" },
    { key: "leads",       label: "Leads" },
    { key: "segments",    label: "Segments" },
    { key: "campaign",    label: "Campaign" },
    { key: "automations", label: "Automations" },
];

const CRMInbound = ({ setHeaderTitle, userId, userProfile }) => {
    const [activeTab, setActiveTab]           = useState("dashboard");
    const [stages, setStages]                 = useState([]);
    const [admins, setAdmins]                 = useState([]);
    const [bootstrapping, setBootstrapping]   = useState(true);
    const [selectedLeadId, setSelectedLeadId] = useState(null);

    // ── Flow state ────────────────────────────────────────────────────────────
    const [flows, setFlows]                     = useState([]);
    const [currentFlowId, setCurrentFlowId]     = useState(null);
    const [autoNodes, setAutoNodes]             = useState([]);
    const [autoConnections, setAutoConnections] = useState([]);
    const [flowsLoaded, setFlowsLoaded]         = useState(false);
    const [saveStatus, setSaveStatus]           = useState("saved");

    const currentFlowIdRef   = useRef(null);
    const autoNodesRef       = useRef([]);
    const autoConnectionsRef = useRef([]);
    const saveTimerRef       = useRef(null);
    const isSwitchingRef     = useRef(false);

    useEffect(() => { currentFlowIdRef.current   = currentFlowId;   }, [currentFlowId]);
    useEffect(() => { autoNodesRef.current        = autoNodes;       }, [autoNodes]);
    useEffect(() => { autoConnectionsRef.current  = autoConnections; }, [autoConnections]);

    useEffect(() => { setHeaderTitle("CRM Panel"); }, []);

    // ── Bootstrap CRM data ────────────────────────────────────────────────────
    useEffect(() => {
        Promise.allSettled([
            apiClient.get("/admin/crm/pipeline-stages"),
            apiClient.get("/admin/users?adminOnly=true"),
        ]).then(([stagesResult, adminsResult]) => {
            if (stagesResult.status === "fulfilled") setStages(stagesResult.value.data.stages || []);
            if (adminsResult.status === "fulfilled") setAdmins(adminsResult.value.data || []);
        }).finally(() => setBootstrapping(false));
    }, []);

    // ── Load flows when automations tab first opens ───────────────────────────
    useEffect(() => {
        if (activeTab !== "automations" || flowsLoaded) return;
        loadFlows();
    }, [activeTab, flowsLoaded]);

    const loadFlows = async () => {
        try {
            const res = await apiClient.get("/admin/crm/automations");
            let list = res.data.automations || [];

            if (list.length === 0) {
                const created = await apiClient.post("/admin/crm/automations", { name: "Untitled Automation" });
                list = [created.data.automation];
            }

            setFlows(list);
            const lastId = parseInt(localStorage.getItem(LAST_FLOW_KEY), 10);
            const target = list.find(f => f.id === lastId) || list[0];
            activateFlow(target);
        } catch (err) {
            console.error("Failed to load automations", err);
            activateFlow({ id: null, name: "Untitled Automation", nodes: [], connections: [] });
        } finally {
            setFlowsLoaded(true);
        }
    };

    const activateFlow = (flow) => {
        isSwitchingRef.current = true;
        setCurrentFlowId(flow.id);
        if (flow.id) localStorage.setItem(LAST_FLOW_KEY, flow.id);

        const nodes       = Array.isArray(flow.nodes)       ? flow.nodes       : (flow.nodes       ? JSON.parse(flow.nodes)       : []);
        const connections = Array.isArray(flow.connections) ? flow.connections : (flow.connections ? JSON.parse(flow.connections) : []);
        setAutoNodes(nodes);
        setAutoConnections(connections);

        setTimeout(() => { isSwitchingRef.current = false; }, 100);
    };
     
    // ── Debounced auto-save ───────────────────────────────────────────────────
    const scheduleSave = useCallback(() => {
        if (isSwitchingRef.current || !currentFlowIdRef.current) return;
        clearTimeout(saveTimerRef.current);
        setSaveStatus("saving");
        saveTimerRef.current = setTimeout(async () => {
            try {
                await apiClient.put(`/admin/crm/automations/${currentFlowIdRef.current}`, {
                    nodes:       autoNodesRef.current,
                    connections: autoConnectionsRef.current,
                });
                setSaveStatus("saved");
            } catch {
                setSaveStatus("error");
            }
        }, SAVE_DEBOUNCE_MS);
    }, []);

    const handleSetNodes = useCallback((nodesOrFn) => {
        setAutoNodes(prev => {
            const next = typeof nodesOrFn === "function" ? nodesOrFn(prev) : nodesOrFn;
            autoNodesRef.current = next;
            scheduleSave();
            return next;
        });
    }, [scheduleSave]);

    const handleSetConnections = useCallback((connsOrFn) => {
        setAutoConnections(prev => {
            const next = typeof connsOrFn === "function" ? connsOrFn(prev) : connsOrFn;
            autoConnectionsRef.current = next;
            scheduleSave();
            return next;
        });
    }, [scheduleSave]);

    // ── Flow CRUD ─────────────────────────────────────────────────────────────
    const handleCreateFlow = async () => {
        try {
            const res = await apiClient.post("/admin/crm/automations", { name: "Untitled Automation" });
            const newFlow = res.data.automation;
            setFlows(prev => [newFlow, ...prev]);
            activateFlow(newFlow);
            return newFlow; // canvas uses this to trigger inline rename
        } catch (err) {
            console.error("Failed to create flow", err);
            return null;
        }
    };

    const handleSwitchFlow = (flow) => {
        if (flow.id === currentFlowId) return;
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            apiClient.put(`/admin/crm/automations/${currentFlowId}`, {
                nodes:       autoNodesRef.current,
                connections: autoConnectionsRef.current,
            }).catch(() => {});
        }
        activateFlow(flow);
    };

    const handleRenameFlow = async (id, name) => {
        try {
            await apiClient.put(`/admin/crm/automations/${id}`, { name });
            setFlows(prev => prev.map(f => f.id === id ? { ...f, name } : f));
        } catch (err) {
            console.error("Failed to rename flow", err);
        }
    };

    const handleDeleteFlow = async (id) => {
        if (flows.length <= 1) return;
        try {
            await apiClient.delete(`/admin/crm/automations/${id}`);
            const next = flows.filter(f => f.id !== id);
            setFlows(next);
            if (id === currentFlowId) activateFlow(next[0]);
        } catch (err) {
            console.error("Failed to delete flow", err);
        }
    };

    const currentFlow = flows.find(f => f.id === currentFlowId);

    // ─────────────────────────────────────────────────────────────────────────
    if (bootstrapping) {
        return (
            <div className="admin-panel">
                <div className="admin-loading-wrapper"><Loading /></div>
            </div>
        );
    }

    if (selectedLeadId) {
        return (
            <div className="admin-panel">
                <div className="admin-table-wrapper">
                    <LeadDetail
                        leadId={selectedLeadId}
                        stages={stages}
                        admins={admins}
                        baseEndpoint={INBOUND_BASE}
                        onBack={() => setSelectedLeadId(null)}
                        userProfile={userProfile}
                        currentUserId={userId}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-panel" style={{ height: "calc(100vh - var(--header-height))", display: "flex", flexDirection: "column" }}>
            <div className="admin-panel-header">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <h1>Inbound</h1>
            </div>

            <div className="admin-table-wrapper" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                <div className="crm-sub-tab-bar" style={{ flexShrink: 0 }}>
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            className={`crm-sub-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === "dashboard"  && <InboundDashboard />}
                {activeTab === "leads"      && <InboundLeadList onSelectLead={setSelectedLeadId} />}
                {activeTab === "segments"   && (
                    <InboundSegments
                        onCreateCampaign={() => setActiveTab("campaign")}
                        onSelectLead={setSelectedLeadId}
                    />
                )}
                {activeTab === "campaign"   && (
                    <InboundCampaignTab />
                )}
                {activeTab === "automations" && (
                    flowsLoaded ? (
                        <AutomationsCanvas
                            key={currentFlowId}
                            nodes={autoNodes}
                            setNodes={handleSetNodes}
                            connections={autoConnections}
                            setConnections={handleSetConnections}
                            flows={flows}
                            currentFlowId={currentFlowId}
                            currentFlowName={currentFlow?.name}
                            saveStatus={saveStatus}
                            onCreateFlow={handleCreateFlow}
                            onSwitchFlow={handleSwitchFlow}
                            onRenameFlow={handleRenameFlow}
                            onDeleteFlow={handleDeleteFlow}
                        />
                    ) : (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Loading />
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default CRMInbound;
